import { useSelector } from 'react-redux';
import { ReactNode } from 'react';
import { useLogger, useRedirectToMainApp, useServices } from '@packages/shared/core/common/hooks';
import { QuizScreenProps, SignInStepData } from '@packages/shared/core/quiz/entities';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { getSignInError, getUserEmail, getUserPreferences } from '@packages/shared/core/user/store/selectors';
import { ApiError, InvalidCredentialsError } from '@packages/shared/core/common/errors';
import type { FormikHelpers } from 'formik';
import { AuthData } from '@packages/shared/core/auth/entities';

type SignInStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const SignInStep = ({ id, renderQuizScreen }: SignInStepProps) => {
  const logger = useLogger();
  const userEmail = useSelector(getUserEmail);
  const singInError = useSelector(getSignInError);
  const userPreferences = useSelector(getUserPreferences);
  const { signInLoadingStatus } = userPreferences;
  const { analyticsService } = useServices();

  const { redirectToExpertsPage } = useRedirectToMainApp();
  const { back, signInUser, stepData, totalSteps, stepOrder } = useQuizStep(id);

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const goBack = async () => back();

  const handleSubmit = async (credentials: AuthData, { setErrors }: FormikHelpers<AuthData>) => {
    try {
      analyticsService.registrationWelcomeBackClick();

      await signInUser(credentials);

      analyticsService.registrationWelcomeBackLoginSuccess();

      redirectToExpertsPage();
    } catch (err) {
      if (err instanceof InvalidCredentialsError) {
        return;
      }

      if (err instanceof ApiError) {
        setErrors({ email: err.originalError.message });
      }
    }
  };

  const trackValidationError = (field: 'password' | 'email') => {
    if (field === 'email') {
      analyticsService.registrationWelcomeBackEmailError();
      return;
    }

    analyticsService.registrationWelcomeBackPasswordError();
  };

  const trackForgotPasswordClick = () => {
    analyticsService.registrationWelcomeBackForgotYourPasswordClick();
  };

  return (
    <>
      {renderQuizScreen({
        onBack: goBack,
        goNext: handleSubmit,
        trackValidationError,
        trackForgotPasswordClick,
        totalSteps,
        stepOrder,
        userEmail,
        singInError,
        signInLoadingStatus,
        ...(stepData as SignInStepData),
      })}
    </>
  );
};

export default SignInStep;
